import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner, ErrorDisplay, EmptyState } from "@/components/loading";
import { useDevices, useAdminUsers } from "@/api/hooks";
import { relativeTime } from "@/lib/format-date";
import { buildDeviceLabelMap } from "@/lib/device-labels";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

export default function DevicesPage() {
  const { data, isLoading, isError, error, refetch } = useDevices();
  const users = useAdminUsers();

  const [userFilter, setUserFilter] = useState("");
  const [manufacturerFilter, setManufacturerFilter] = useState("");
  const [androidFilter, setAndroidFilter] = useState("");
  const [page, setPage] = useState(0);

  // Build sub -> username map for display
  const subToUsername = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users.data?.data ?? []) {
      map[u.sub] = u.username;
    }
    return map;
  }, [users.data]);

  // Build device_id -> "Device N" label (ordinal per user by first_seen)
  const deviceLabels = useMemo(
    () => buildDeviceLabelMap(data?.data ?? []),
    [data]
  );


  const filtered = useMemo(() => {
    return (data?.data ?? []).filter((d) => {
      if (userFilter && d.user_id !== userFilter) return false;
      if (manufacturerFilter && !d.manufacturer.toLowerCase().includes(manufacturerFilter.toLowerCase())) return false;
      if (androidFilter && !d.android_version.includes(androidFilter)) return false;
      return true;
    });
  }, [data, userFilter, manufacturerFilter, androidFilter]);

  const paginated = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  if (isLoading) return <Spinner />;
  if (isError) return <ErrorDisplay message={error.message} onRetry={() => refetch()} />;

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#1E293B]">Devices</h1>
        <span className="text-sm text-muted-foreground">{filtered.length} devices</span>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">User</label>
          <Select
            value={userFilter}
            onValueChange={(v) => { setUserFilter(v === "__all__" ? "" : (v ?? "")); setPage(0); }}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All users</SelectItem>
              {(users.data?.data ?? []).map((u) => (
                <SelectItem key={u.sub} value={u.sub}>{u.username}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Manufacturer</label>
          <Input
            placeholder="e.g. samsung"
            value={manufacturerFilter}
            onChange={(e) => { setManufacturerFilter(e.target.value); setPage(0); }}
            className="w-36"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Android Version</label>
          <Input
            placeholder="e.g. 14"
            value={androidFilter}
            onChange={(e) => { setAndroidFilter(e.target.value); setPage(0); }}
            className="w-28"
          />
        </div>
      </div>

      {paginated.length === 0 ? (
        <EmptyState message="No devices found" />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device ID</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Manufacturer</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Android</TableHead>
                <TableHead>App Version</TableHead>
                <TableHead>Last Seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map((device) => {
                return (
                <TableRow key={device.device_id}>
                  <TableCell className="text-xs font-medium" title={device.device_id}>
                    {deviceLabels[device.device_id] ?? `Device ?`}
                  </TableCell>
                  <TableCell>
                    {device.user_id ? (
                      <Link
                        to={`/participants/${device.user_id}`}
                        className="text-[#2563EB] hover:underline font-mono text-xs"
                      >
                        {subToUsername[device.user_id] ?? device.user_id.slice(0, 8)}
                      </Link>
                    ) : "-"}
                  </TableCell>
                  <TableCell className="capitalize">{device.manufacturer || "-"}</TableCell>
                  <TableCell>{device.model || "-"}</TableCell>
                  <TableCell>{device.android_version || "-"}</TableCell>
                  <TableCell>{device.app_version || "-"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {device.last_seen ? relativeTime(device.last_seen) : "-"}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="size-4" /> Prev
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  Next <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
